import type { ClientPostView } from '@DigitaleDoerfer/digitale-doerfer-api';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { storage } from '@/configs/storage';
import { MILLISECONDS_UNTIL_POST_VIEW_REMOVED } from '@/shared/constants/newPosts';
import { POST_VIEW_STORE_KEY } from '@/shared/constants/store';
import { isEmptyOrNull } from '@/shared/utils/string';

export type PostViewStore = {
  postViews: ClientPostView[];
  lastSent: number;
  updateLastSent: () => void;
  getPostViewsSinceLastSent: () => ClientPostView[];
  addPostDetailView: (postId?: string) => void;
  addPostOverviewView: (postId?: string) => void;
  addPostOverviewViews: (postIds: (string | undefined)[]) => void;
  expirePostViews: () => void;
  reset: () => void;
};

export const usePostViewStore = create<PostViewStore>()(
  persist(
    (set, get) => ({
      postViews: [],
      lastSent: 0,
      updateLastSent: () => {
        set({ lastSent: Date.now() });
      },
      getPostViewsSinceLastSent: () => {
        const currentLastSent = get().lastSent;
        return get().postViews.filter(
          (v) =>
            (v.viewedDetailTime ?? 0) > currentLastSent ||
            (v.viewedOverviewTime ?? 0) > currentLastSent
        );
      },
      addPostDetailView: (postId?: string) => {
        if (isEmptyOrNull(postId)) {
          return;
        }
        const timestamp = Date.now();
        const currentViews = get().postViews;
        const view = currentViews.find((v) => v.postId === postId);
        if (view?.viewedDetailTime) {
          return; // keep first view
        } else if (view) {
          // update existing view with missing timestamp
          set({
            postViews: [
              ...currentViews.filter((v) => v.postId !== postId),
              {
                postId,
                viewedDetailTime: timestamp,
                viewedOverviewTime: view.viewedOverviewTime
              }
            ]
          });
        } else {
          // add new view
          set({
            postViews: [
              ...currentViews,
              { postId, viewedDetailTime: timestamp }
            ]
          });
        }
      },
      addPostOverviewView: (postId?: string) => {
        if (isEmptyOrNull(postId)) {
          return;
        }
        const timestamp = Date.now();
        const currentViews = get().postViews;
        const view = currentViews.find((v) => v.postId === postId);
        if (view?.viewedOverviewTime) {
          return; // keep first view
        } else if (view) {
          // update existing view with missing timestamp
          set({
            postViews: [
              ...currentViews.filter((v) => v.postId !== postId),
              {
                postId,
                viewedDetailTime: view.viewedDetailTime,
                viewedOverviewTime: timestamp
              }
            ]
          });
        } else {
          // add new view
          set({
            postViews: [
              ...currentViews,
              { postId, viewedOverviewTime: timestamp }
            ]
          });
        }
      },
      addPostOverviewViews: (postIds: (string | undefined)[]) => {
        if (postIds.length === 0) return;
        const timestamp = Date.now();
        let views = get().postViews;
        for (const postId of postIds) {
          if (isEmptyOrNull(postId)) {
            continue;
          }
          const view = views.find((v) => v.postId === postId);
          if (view?.viewedOverviewTime) {
            continue; // keep first view
          } else if (view) {
            // update existing view with missing timestamp
            views = [
              ...views.filter((v) => v.postId !== postId),
              {
                postId,
                viewedDetailTime: view.viewedDetailTime,
                viewedOverviewTime: timestamp
              }
            ];
          } else {
            // add new view
            views = [...views, { postId, viewedOverviewTime: timestamp }];
          }
        }
        set({
          postViews: views
        });
      },
      expirePostViews: () => {
        const currentViews = get().postViews;
        const deadline = Date.now() - MILLISECONDS_UNTIL_POST_VIEW_REMOVED;
        const newViews = currentViews.filter(
          (v) =>
            (v.viewedDetailTime ?? 0) > deadline &&
            (v.viewedOverviewTime ?? 0) > deadline
        );
        if (newViews.length === currentViews.length) return;
        set({ postViews: newViews });
      },
      reset: () => {
        set({
          postViews: []
        });
      }
    }),
    {
      name: POST_VIEW_STORE_KEY,
      storage: createJSONStorage(() => storage)
    }
  )
);
